import { type ReactNode } from "react";
import { Badge, Button, Input, Space, Typography } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "./PageHeader";
import { ResponsiveTable } from "./ResponsiveTable";
import { TableDownload } from "./TableDownload";
import { PrintFilters } from "./PrintFilters";
import { usePaginatedList } from "../helpers/usePaginatedList";
import type { ExportColumns } from "../helpers/csv";

/** What a `filters` render function gets: the live value of a URL filter, and a setter. */
export interface ListFilterControls {
  /** Current value of a filter param — pending keystrokes included. */
  value: (key: string) => string | undefined;
  /** Same, coerced for Selects whose options carry numeric values (ids). */
  num: (key: string) => number | undefined;
  /** Writes the param to the URL after a 300ms debounce; the control updates immediately. */
  set: (key: string, value?: string | number | null) => void;
  /**
   * Writes several params in one navigation, undebounced — for a control that owns more
   * than one of them (a date range writes `from` and `to`). Two `set` calls in the same
   * handler would not compose; see setParams in usePaginatedList.
   */
  setMany: (patch: Record<string, string | number | null | undefined>) => void;
}

interface ListPageProps<T> {
  title: string;
  endpoint: string;
  columns: ExportColumns<T>;
  searchPlaceholder?: string;
  filters?: ReactNode | ((controls: ListFilterControls) => ReactNode);
  extraParams?: Record<string, string | number | boolean | undefined>;
  actions?: ReactNode;
  rowKey?: string;
  linkTo?: (record: T) => string;
  renderCard?: (record: T) => ReactNode;
  /** Registers export by default; set false where a CSV of the list makes no sense. */
  exportable?: boolean;
  /**
   * Trades the page-number strip for a "Load more" button, for endpoints whose total row
   * count is too expensive to ask for (the audit log). See PaginatedListOptions.loadMore.
   */
  loadMore?: boolean;
  /** Filter values as a reader should see them, when the raw params are ids. */
  exportFilters?: Record<string, unknown>;
}

// The list template used by ~15 of the app's pages (architecture.md §11).
export function ListPage<T extends object>({
  title,
  endpoint,
  columns,
  searchPlaceholder,
  filters,
  extraParams,
  actions,
  rowKey = "id",
  linkTo,
  renderCard,
  exportable = true,
  exportFilters,
  loadMore = false,
}: ListPageProps<T>) {
  const {
    data,
    count,
    loading,
    loadingMore,
    hasMore,
    loadMore: loadMoreRows,
    page,
    pageSize,
    search,
    sort,
    order,
    filters: urlFilters,
    activeFilterCount,
    clearFilters,
    setParams,
    setDebouncedParam,
    paramValue,
  } = usePaginatedList<T>(endpoint, extraParams, { loadMore });
  const navigate = useNavigate();

  // One description of the view, read by both the export header and the printed page.
  const appliedFilters = exportFilters ?? { search, ...urlFilters, ...extraParams };

  const filterNum = (key: string) => {
    const v = paramValue(key);
    return v === undefined ? undefined : Number(v);
  };

  return (
    <div>
      <PageHeader
        title={title}
        extra={
          <Space>
            {/* Exports the whole filtered register, not just the page on screen. */}
            {exportable && (
              <TableDownload<T>
                name={title}
                columns={columns}
                endpoint={endpoint}
                params={{ search: search || undefined, sort: sort || undefined, order: order || undefined, ...urlFilters, ...extraParams }}
                filters={appliedFilters}
                disabled={data.length === 0}
              />
            )}
            {actions}
          </Space>
        }
      />
      <div className="glut-filter-bar">
        <Input
          placeholder={searchPlaceholder ?? "Search..."}
          value={paramValue("search") ?? ""}
          onChange={(e) => setDebouncedParam("search", e.target.value)}
          allowClear
          style={{ maxWidth: 280 }}
        />
        {typeof filters === "function"
          ? filters({ value: paramValue, num: filterNum, set: setDebouncedParam, setMany: setParams })
          : filters}
        {/* Stays put (disabled) with nothing applied so the bar's controls never shift under the cursor. */}
        <Badge count={activeFilterCount} size="small" offset={[-4, 2]}>
          <Button icon={<ClearOutlined />} onClick={clearFilters} disabled={activeFilterCount === 0}>
            Clear filters
          </Button>
        </Badge>
      </div>
      <PrintFilters filters={appliedFilters} />
      <div data-tour="list-table">
      <ResponsiveTable
        rowKey={rowKey}
        loading={loading}
        columns={columns}
        dataSource={data}
        renderCard={renderCard}
        onRowClick={linkTo ? (record) => navigate(linkTo(record)) : undefined}
        sort={{
          key: sort,
          order: order === "desc" ? "desc" : order === "asc" ? "asc" : "",
          onChange: (key, dir) => setParams({ sort: key ?? null, order: dir ?? null }),
        }}
        pagination={
          loadMore
            ? undefined
            : {
                current: page,
                pageSize,
                total: count,
                // Both in one update — see setParams in usePaginatedList.
                onChange: (p, ps) => setParams({ page: p, pageSize: ps }),
              }
        }
      />
      </div>
      {/* No total to show, so the footer says what has been read rather than what is left. */}
      {loadMore && data.length > 0 && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Space direction="vertical" size={4}>
            {hasMore ? (
              <Button onClick={loadMoreRows} loading={loadingMore}>
                Load more
              </Button>
            ) : (
              <Typography.Text type="secondary">End of the list.</Typography.Text>
            )}
            <Typography.Text type="secondary">{data.length.toLocaleString()} loaded</Typography.Text>
          </Space>
        </div>
      )}
    </div>
  );
}
